import { formatarDia } from '@common/i18n/formatosData';
import { Badge, Button } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { FiltrosPainel } from '../types/Painel';

type Chave = keyof FiltrosPainel;
/** `valor`: o item do filtro de lista que este chip representa (um chip por valor, spec 24 B). */
type Chip = { chave: Chave; texto: string; valor?: string };

/** Chips removíveis dos filtros aplicados e "Limpar tudo" (spec 14 R10). */
export function ParticipantesChips({
  filtros,
  onRemover,
  onLimpar,
}: {
  filtros: FiltrosPainel;
  onRemover: (parcial: Partial<FiltrosPainel>) => void;
  onLimpar: () => void;
}) {
  const { t } = useTranslation(['participantes', 'convites']);
  const rot = (k: string) => t(`participantes:${k}` as never);
  const f = filtros;

  const chips: Chip[] = [];
  const add = (chave: Chave, rotulo: string, valor: string, item?: string) =>
    chips.push({
      chave,
      texto: t('participantes:painel.filtros.rotulo', { filtro: rotulo, valor }),
      valor: item,
    });
  const cada = (
    chave: Chave,
    rotulo: string,
    valores: readonly string[],
    nome: (v: string) => string,
  ) => {
    for (const v of valores) add(chave, rotulo, nome(v), v);
  };
  const simNao = (v: string) => rot(`painel.filtros.${v}`);

  if (f.busca) add('busca', rot('painel.filtros.buscaChip'), f.busca);
  if (f.matricula) add('matricula', rot('painel.colunas.matricula'), f.matricula);
  if (f.nome) add('nome', rot('painel.colunas.nome'), f.nome);
  if (f.email) add('email', rot('colunas.email'), f.email);
  cada('temEmail', rot('filtros.temEmail'), f.temEmail, simNao);
  cada('respondeu', rot('painel.colunas.resposta'), f.respondeu, simNao);
  cada('brinde', rot('painel.colunas.brinde'), f.brinde, simNao);
  cada('axiis', rot('painel.filtros.axiis'), f.axiis, simNao);
  cada('convite', rot('painel.colunas.convite'), f.convite, (v) =>
    t(`convites:status.${v}` as never),
  );
  cada('camiseta', rot('painel.colunas.camiseta'), f.camiseta, (v) => v);
  cada('idioma', rot('colunas.idioma'), f.idioma, (v) => rot(`idiomas.${v}`));
  cada('entrega', rot('painel.colunas.endereco'), f.entrega, (v) => rot(`painel.entrega.${v}`));
  cada('acessou', rot('filtros.acessou'), f.acessou, simNao);
  if (f.incluidoOrigem)
    add('incluidoOrigem', rot('colunas.origem'), rot(`origem.${f.incluidoOrigem}`));
  const periodo = (de: string, ate: string) =>
    [de && formatarDia(de), ate && formatarDia(ate)].filter(Boolean).join(' – ');
  if (f.respondidaDe || f.respondidaAte)
    add('respondidaDe', rot('filtros.respondidaEm'), periodo(f.respondidaDe, f.respondidaAte));
  if (f.acessoDe || f.acessoAte)
    add('acessoDe', rot('colunas.ultimoAcesso'), periodo(f.acessoDe, f.acessoAte));

  if (chips.length === 0) return null;

  /** Cada chip zera o(s) campo(s) do filtro que o originou; o de lista tira só o seu valor. */
  const zerar = ({ chave: c, valor }: Chip): Partial<FiltrosPainel> => {
    const atual = f[c];
    if (Array.isArray(atual)) return { [c]: atual.filter((v) => v !== valor) };
    if (c === 'respondidaDe') return { respondidaDe: '', respondidaAte: '' };
    if (c === 'acessoDe') return { acessoDe: '', acessoAte: '' };
    return { [c]: '' };
  };

  return (
    <fieldset className="flex min-w-0 flex-wrap items-center gap-2">
      <legend className="sr-only">{rot('filtros.aplicados')}</legend>
      {chips.map((c) => (
        <Badge
          key={`${c.chave}:${c.valor ?? ''}`}
          variant="outline"
          className="min-w-0 max-w-full"
          title={c.texto}
          onRemove={() => onRemover(zerar(c))}
          removeLabel={t('participantes:filtros.remover', { filtro: c.texto })}
        >
          <span className="min-w-0 truncate">{c.texto}</span>
        </Badge>
      ))}
      <Button variant="secondary" size="sm" onClick={onLimpar}>
        {t('participantes:filtros.limparTudo')}
      </Button>
    </fieldset>
  );
}
